import { createContext, useContext } from "react";
import type { Order } from "./types";

/**
 * Dostęp ZALOGOWANEJ osoby do oglądanego zamówienia — jeden kontekst dla karty GK
 * (OrderDetailPage) i jej sekcji (wnioski o zmianę, koszty, komentarze…).
 *
 * Źródło: pola `access_level` / `access_via_grant` / `can_manage` zamówienia
 * (backend: apps.orders.access). Sekcje sprawdzają go obok roli użytkownika:
 * - `readOnly` — dostęp „Podgląd”: żadnych akcji, formularzy ani mutacji;
 * - `canManage` — akcje jak u zamawiającego (także grant „Zarządzanie” u pracownika,
 *   który z roli nie jest ani koordynatorem, ani dyrektorem).
 */
export interface OrderAccessState {
  readOnly: boolean;
  canManage: boolean;
  amountsHidden: boolean;
}

const DEFAULT_STATE: OrderAccessState = { readOnly: false, canManage: false, amountsHidden: false };

const OrderAccessContext = createContext<OrderAccessState>(DEFAULT_STATE);

export const OrderAccessProvider = OrderAccessContext.Provider;

export function useOrderAccess(): OrderAccessState {
  return useContext(OrderAccessContext);
}

/** Stan kontekstu policzony z pól zamówienia (null = domyślny, bez ograniczeń). */
export function orderAccessState(order: Pick<Order, "access_level" | "access_via_grant" | "can_manage" | "amounts_hidden"> | null | undefined): OrderAccessState {
  if (!order) return DEFAULT_STATE;
  return {
    readOnly: order.access_via_grant && order.access_level === "view",
    canManage: !!order.can_manage,
    amountsHidden: !!order.amounts_hidden,
  };
}

/** Grant „Realizacja” — karta przełącza się na widok podwykonawcy (bez kwot). */
export function isExecutionAccess(order: Pick<Order, "access_level" | "access_via_grant"> | null | undefined): boolean {
  return !!order && order.access_via_grant && order.access_level === "execute";
}
